import React, { useEffect, useState, useMemo, useCallback } from 'react';
import './RIADashboardPage.scss';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
} from 'recharts';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa';
import { getRIADashboard } from './api/rewrites';
import { Loader } from '@shared/UI';
import { HoldingTable } from '@widgets/HoldingTable';
import { useNavigate, useParams } from 'react-router-dom';
import { useSessionStore } from '@pages/AccountDetails/store/SessionIdStore';
import { fetchFullHistory, fetchStockQuotes } from '@pages/AccountDetails/api/rewrites';
import { useStockPerformanceStore } from './store/stock-performance-store';

// Constants
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const QUARTERS = ['Jan-Mar', 'Apr-Jun', 'Jul-Sep', 'Oct-Dec'];

export const RIADashboardPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { bestPerforming, worstPerforming } = useStockPerformanceStore();
    const { sessionId, initializeSession, refreshSession } = useSessionStore();

    // State
    const [dashboardData, setDashboardData] = useState<any>(null);
    const [selectedClientList, setSelectedClientList] = useState<any>(null);
    const [portfolioValue, setPortfolioValue] = useState<number>(0);
    const [amountReturned, setAmountReturned] = useState<number>(0);
    const [aumChartData, setAumChartData] = useState<{ name: string; value: number }[]>([]);
    const [returnChartData, setReturnChartData] = useState<{ day: string; return: number }[]>([]);
    const [combinedLosses, setCombinedLosses] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    // Get user data from localStorage
    const userObject = useMemo(() => {
        const userStorage = localStorage.getItem('User');
        return userStorage ? JSON.parse(userStorage) : {};
    }, []);

    // Format large numbers for display
    const formatLargeNumber = useCallback((value: number) => {
        if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
        if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}k`;
        return `$${value}`;
    }, []);

    // Format amount with integer and decimal parts
    const formatAmount = useCallback((amount: number) => {
        const integerPart = Math.floor(amount).toLocaleString();
        const decimalPart = (amount % 1).toFixed(2).slice(2);
        return { integerPart, decimalPart };
    }, []);

    // Process AUM data for chart
    const processAumData = useCallback((aumDatas: any[]) => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth() + 1; // 1-based index

        // Get last 6 months (including current)
        const lastSixMonths = Array.from({ length: 7 }, (_, i) => {
            const monthDiff = 6 - i;
            let monthIndex = currentMonth - monthDiff;
            let year = currentYear;

            if (monthIndex <= 0) {
                monthIndex += 12;
                year -= 1;
            }

            return { name: MONTHS[monthIndex - 1], key: `${year}-${monthIndex}` };
        });

        // Convert AUM data to a map
        const aumMap = new Map(
            aumDatas.map((entry: any) => {
                const date = new Date(entry.month);
                return [`${date.getFullYear()}-${date.getMonth() + 1}`, entry.totalAUM];
            }),
        );

        // Format data for the chart
        return lastSixMonths.map(({ name, key }) => ({
            name,
            value: Number(aumMap.get(key)) || 0,
        }));
    }, []);

    // Fetch portfolio values based on historical data
    const fetchPortfolioValues = useCallback(async (sessionId: string, stockHoldings: any[]) => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 7);

        const endStr = endDate.toISOString().split('T')[0];
        const startStr = startDate.toISOString().split('T')[0];

        // Extract unique stock symbols
        const symbols = Array.from(
            new Set(stockHoldings.flatMap((entry) => entry.stocks.map((stock: any) => stock.symbol))),
        );

        // Fetch historical stock prices
        const historyData: Record<string, any[]> = {};
        await Promise.all(
            symbols.map(async (symbol) => {
                const response = await fetchFullHistory(symbol as string, sessionId, startStr, endStr);
                const history = response?.results?.history?.[0]?.eoddata || [];

                historyData[symbol as string] = history.map((item: any) => ({
                    date: new Date(item.date).toISOString().split('T')[0],
                    price: item.close ?? 0,
                }));
            }),
        );

        // Fetch today's stock prices
        const currentResponse = await fetchStockQuotes(symbols.join(','), sessionId);
        const todayPriceMap: Record<string, number> = {};

        (currentResponse?.quotedata || []).forEach((stock: any) => {
            todayPriceMap[stock.symbol] = stock.pricedata?.last ?? 0;
        });

        // Calculate daily portfolio values
        const portfolioValues = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date();
            date.setDate(date.getDate() - (7 - i));
            const dateStr = date.toISOString().split('T')[0];

            const dailyHolding = stockHoldings.find((h) => h.date.startsWith(dateStr));
            const totalValue =
                dailyHolding?.stocks.reduce((sum: number, stock: any) => {
                    const stockPrice = historyData[stock.symbol]?.find((h) => h.date === dateStr)?.price ?? 0;
                    return sum + stockPrice * (stock.totalQuantity ?? 0);
                }, 0) || 0;

            portfolioValues.push({ date: dateStr, return: totalValue });
        }

        // Add today's portfolio value
        const todayStr = endStr;
        const todayHolding = stockHoldings.find((h) => h.date.startsWith(todayStr));
        const todayValue =
            todayHolding?.stocks.reduce((sum: number, stock: any) => {
                return sum + (todayPriceMap[stock.symbol] ?? 0) * (stock.totalQuantity ?? 0);
            }, 0) || 0;

        portfolioValues.push({ date: todayStr, return: todayValue });

        return portfolioValues;
    }, []);

    // Adjust portfolio values for chart display
    const adjustPortfolioValues = useCallback((portfolioValues: any[], totalPortfolioValue: number) => {
        return portfolioValues.slice(1).map(({ date, return: portfolioReturn }) => {
            const dayOfWeek = new Date(date).toLocaleDateString('en-US', { weekday: 'short' });
            return {
                day: dayOfWeek,
                return: portfolioReturn === 0 ? 0 : portfolioReturn - totalPortfolioValue,
            };
        });
    }, []);

    // Process new money data for chart
    const processNewMoneyData = useCallback((transactions: any[]) => {
        const quarterlyData = transactions.reduce(
            (acc: Record<number, { name: string; value: number }>, transaction) => {
                const date = new Date(transaction.transactionDate);
                const quarterIndex = Math.floor(date.getMonth() / 3);

                if (!acc[quarterIndex]) {
                    acc[quarterIndex] = { name: QUARTERS[quarterIndex], value: 0 };
                }

                acc[quarterIndex].value += transaction.price;
                return acc;
            },
            {},
        );

        return QUARTERS.map((quarter, index) => ({
            name: quarter,
            value: quarterlyData?.[index]?.value ?? 0,
        }));
    }, []);

    // Main data fetch effect
    useEffect(() => {
        const fetchData = async () => {
            if (!sessionId) {
                await initializeSession();
                return;
            }

            setLoading(true);
            try {
                const response = await getRIADashboard(userObject?.accounts?.[0]?.id || id);
                if (!response) return;

                setDashboardData(response);

                // Set initial client selection
                if (response.clientPortfolios?.length > 0) {
                    setSelectedClientList(response.clientPortfolios[0]);
                    setPortfolioValue(response.clientPortfolios[0].portfolioValue);
                }

                // Process AUM data
                if (response.aumDatas) {
                    setAumChartData(processAumData(response.aumDatas));
                }

                // Process stock data
                const symbols = response.stockHoldings.map((stock: any) => stock.symbol).join(',');
                const quoteResult = await fetchStockQuotes(symbols, sessionId);

                if (quoteResult === null) {
                    await refreshSession();
                    return;
                }

                // Calculate returns
                const stockMap = new Map(
                    quoteResult.quotedata.map((stock: any) => [stock.symbol, stock.pricedata?.last ?? 0]),
                );

                const totalReturn = response.stockHoldings.reduce((sum: number, stock: any) => {
                    const latestPrice = Number(stockMap.get(stock.symbol) ?? 0);
                    const quantity = Number(stock.totalQuantity ?? 0);
                    return sum + (latestPrice * quantity - Number(stock.totalValue ?? 0));
                }, 0);

                setAmountReturned(totalReturn);

                // Process historical data
                const stockHoldingsByDate = response.stockHoldingsByDate || [];
                const formattedHoldings = stockHoldingsByDate.map((entry: any) => ({
                    date: entry.date,
                    stocks: entry.holdings.map((stock: any) => ({
                        symbol: stock.symbol,
                        totalQuantity: stock.totalQuantity,
                    })),
                }));

                const totalPortfolioValue = response.stockHoldings.reduce(
                    (sum: number, stock: any) => sum + (stock.totalValue ?? 0),
                    0,
                );

                const portfolioValues = await fetchPortfolioValues(sessionId, formattedHoldings);
                setReturnChartData(adjustPortfolioValues(portfolioValues, totalPortfolioValue));

                // Process losses
                const losses = (response.stockHoldings || [])
                    .map((stock: any) => {
                        const symbol = stock.symbol;
                        const totalQuantity = Number(stock.totalQuantity ?? 0);
                        const totalValue = Number(stock.totalValue ?? 0);
                        const latestPrice = Number(stockMap.get(symbol) ?? 0);
                        const averagePrice = totalQuantity !== 0 ? totalValue / totalQuantity : 0;

                        const unrealizedLoss =
                            latestPrice < averagePrice ? (averagePrice - latestPrice) * totalQuantity : 0;

                        const realizedLossEntry = response.losses?.find((l: any) => l.symbol === symbol);
                        const realizedLoss = realizedLossEntry?.realizedLoss ?? 0;

                        return {
                            symbol,
                            realizedLoss,
                            unrealizedLoss,
                            totalLoss: realizedLoss + unrealizedLoss,
                        };
                    })
                    .sort((a: any, b: any) => b.totalLoss - a.totalLoss) // Sort descending by totalLoss
                    .slice(0, 10); // Take top 10

                setCombinedLosses(losses);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [
        sessionId,
        id,
        userObject,
        initializeSession,
        refreshSession,
        processAumData,
        fetchPortfolioValues,
        adjustPortfolioValues,
    ]);

    // Memoized derived values
    const { integerPart: AUMAmount, decimalPart: AUMDecimal } = useMemo(
        () => formatAmount(dashboardData?.totalAUM || 0),
        [dashboardData?.totalAUM, formatAmount],
    );

    const { integerPart: returnAmount, decimalPart: returnDecimal } = useMemo(
        () => formatAmount(amountReturned || 0),
        [amountReturned, formatAmount],
    );

    const returnPercentage = useMemo(
        () => (amountReturned / (dashboardData?.totalAUM || 1)) * 100,
        [amountReturned, dashboardData?.totalAUM],
    );

    const stockData = useMemo(() => {
        const stocks = (dashboardData?.stockHoldings || [])
            .sort((a: any, b: any) => b.totalValue - a.totalValue) // sort descending by totalValue
            .slice(0, 10) // pick top 10
            .map((stock: any) => ({
                name: stock.symbol,
                value: stock.totalValue,
            }));
        return stocks;
    }, [dashboardData?.stockHoldings]);

    const newMoneyData = useMemo(
        () => processNewMoneyData(dashboardData?.newMoneyTransactions || []),
        [dashboardData?.newMoneyTransactions, processNewMoneyData],
    );

    const pieData = useMemo(
        () => [
            { name: 'Investment', value: dashboardData?.totalInvestment, color: '#0A66C2' },
            { name: 'Cash', value: dashboardData?.totalCash, color: '#F9935D' },
        ],
        [dashboardData?.totalInvestment, dashboardData?.totalCash],
    );

    const { integerPart: portfolioInteger, decimalPart: portfolioDecimal } = useMemo(
        () => formatAmount(portfolioValue),
        [portfolioValue, formatAmount],
    );

    if (loading || !dashboardData) {
        return <Loader />;
    }

    return (
        <div className='dashboard'>
            <div className='dashboard__grid'>
                {/* Top Row */}
                <div className='flex-only'>
                    {/* Total AUM Card */}
                    <div className='dashboard__card'>
                        <div className='metriced'>
                            <div className='flex-only'>
                                <div className='metriced__title'>AUM</div>
                            </div>
                            <div className='metriced__value'>
                                <span className='primary'>${AUMAmount}</span>
                                <span className='secondary'>.{AUMDecimal}</span>
                            </div>
                        </div>
                        <div className='metriced__chart'>
                            <div className='dotted-line line-1'></div>
                            <div className='dotted-line line-2'></div>
                            <ResponsiveContainer
                                width='100%'
                                height={120}
                            >
                                <LineChart
                                    data={aumChartData}
                                    margin={{ top: 5, right: 0, bottom: 5, left: 0 }}
                                >
                                    <Line
                                        type='monotone'
                                        dataKey='value'
                                        stroke='#6FC68B'
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                    <XAxis
                                        dataKey='name'
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 10 }}
                                    />
                                    <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Return Card */}
                    <div className='dashboard__card'>
                        <div className='metriced'>
                            <div className='metriced__title'>Return</div>
                            <div className='metriced__value'>
                                <span className='primary'>${returnAmount}</span>
                                <span className='secondary'>{returnDecimal}</span>
                                <span
                                    className={`arrow ${returnPercentage > 0 ? 'positive-arrows' : 'negative-arrows'}`}
                                >
                                    {returnPercentage > 0 ? <FaCaretUp /> : <FaCaretDown />}
                                    {returnPercentage.toFixed(2)}%
                                </span>
                            </div>
                        </div>
                        <div className='metriced__chart'>
                            <div className='dotted-line line-1'></div>
                            <div className='dotted-line line-2'></div>
                            <ResponsiveContainer
                                width='100%'
                                height={120}
                            >
                                <LineChart
                                    data={returnChartData}
                                    margin={{ top: 5, right: 0, bottom: 5, left: 0 }}
                                >
                                    <Line
                                        type='monotone'
                                        dataKey='return'
                                        stroke={returnPercentage > 0 ? '#6FC68B' : '#ff9e9e'}
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                    <XAxis
                                        dataKey='day'
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 10 }}
                                        padding={{ left: 10 }}
                                    />
                                    <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Stock Holdings Card */}
                    <div className='dashboard__card'>
                        <div className='metriced__title'>Stock Holdings</div>
                        <div className='stock-chart'>
                            <ResponsiveContainer
                                width='100%'
                                height={180}
                            >
                                <BarChart
                                    data={stockData}
                                    margin={{ top: 10, right: 10, left: -20, bottom: -10 }}
                                >
                                    <CartesianGrid
                                        vertical={false}
                                        strokeDasharray='3 3'
                                        stroke='rgba(0, 0, 0, 0.1490196078)'
                                    />
                                    <XAxis
                                        dataKey='name'
                                        axisLine={false}
                                        tickLine={false}
                                        interval={0}
                                        tick={{ fontSize: 10 }}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tickFormatter={formatLargeNumber}
                                        tick={{ fontSize: 10 }}
                                    />
                                    <Tooltip
                                        formatter={(value) => [`$${value.toLocaleString()}`, 'Value']}
                                        labelFormatter={(label) => `${label}`}
                                        cursor={{ fill: 'transparent' }}
                                    />
                                    <Bar
                                        dataKey='value'
                                        radius={[6, 6, 6, 6]}
                                        barSize={12}
                                        onMouseLeave={() => setActiveIndex(null)}
                                    >
                                        {stockData.map((entry: any, index: any) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={activeIndex === index ? '#0A66C2' : '#e0e0e0'}
                                                onMouseEnter={() => setActiveIndex(index)}
                                                fillOpacity={1}
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Middle Row */}
                <div className='flex-only'>
                    {/* New Money Card */}
                    <div className='dashboard__card'>
                        <div className='metriced__title'>New Money</div>
                        <div className='line-chart'>
                            <ResponsiveContainer
                                width='100%'
                                height={200}
                            >
                                <LineChart
                                    data={newMoneyData}
                                    margin={{ top: 20, right: 40, bottom: -10, left: -20 }}
                                >
                                    <CartesianGrid
                                        strokeDasharray='3 3'
                                        stroke='#d3d3d3'
                                        horizontal={true}
                                        vertical={true}
                                    />
                                    <XAxis
                                        dataKey='name'
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fontSize: 10, textAnchor: 'start' }}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tickFormatter={formatLargeNumber}
                                        tick={{ fontSize: 10 }}
                                    />
                                    <Tooltip
                                        formatter={(value) => [
                                            <span style={{ color: '#0066a6', fontWeight: 600 }}>
                                                ${value.toLocaleString()}
                                            </span>,
                                        ]}
                                        labelFormatter={(label) => `${label}`}
                                    />
                                    <Line
                                        type='monotone'
                                        dataKey='value'
                                        stroke='#0A66C2'
                                        strokeWidth={2}
                                        dot={{ stroke: '#0A66C2', strokeWidth: 2, fill: 'white', r: 4 }}
                                        activeDot={{ r: 6 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Losses Card */}
                    <div className='dashboard__card'>
                        <div className='losses-section'>
                            <div className='losses-title'>Losses</div>
                            <div className='losses-legend'>
                                <div className='losses-legend-item'>
                                    <span className='losses-legend-color losses-realized'></span>
                                    <span className='losses-legend-label'>Realized</span>
                                </div>
                                <div className='losses-legend-item'>
                                    <span className='losses-legend-color losses-unrealized'></span>
                                    <span className='losses-legend-label'>Unrealized</span>
                                </div>
                            </div>
                        </div>
                        <div className='stock-chart'>
                            <ResponsiveContainer
                                width='100%'
                                height={200}
                            >
                                <BarChart
                                    data={combinedLosses}
                                    margin={{ top: 10, right: 10, left: -20, bottom: -10 }}
                                >
                                    <CartesianGrid
                                        vertical={false}
                                        strokeDasharray='3 3'
                                        stroke='rgba(0, 0, 0, 0.1490196078)'
                                    />
                                    <XAxis
                                        dataKey='symbol'
                                        axisLine={false}
                                        tickLine={false}
                                        interval={0}
                                        tick={{ fontSize: 10 }}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tickFormatter={formatLargeNumber}
                                        tick={{ fontSize: 10 }}
                                    />
                                    <Tooltip
                                        formatter={(value: number, name: string) => {
                                            return [`$${value.toLocaleString()}`, name];
                                        }}
                                        labelFormatter={(label: string) => `${label}`}
                                        cursor={{ fill: 'transparent' }}
                                    />
                                    <Bar
                                        dataKey='realizedLoss'
                                        fill='#0066A6'
                                        name='Realized Loss'
                                        barSize={12}
                                        radius={[6, 6, 6, 6]}
                                    />
                                    <Bar
                                        dataKey='unrealizedLoss'
                                        fill='#FF9E69'
                                        name='Unrealized Loss'
                                        barSize={12}
                                        radius={[6, 6, 6, 6]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Pie Chart Card */}
                    <div className='dashboard__card'>
                        <div className='pie-chart'>
                            <ResponsiveContainer
                                width='100%'
                                height={200}
                            >
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx='50%'
                                        cy='50%'
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={0}
                                        dataKey='value'
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={entry.color}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        formatter={(value) => [
                                            `$${value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                                            'Value',
                                        ]}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className='pie-legend'>
                                {pieData.map((entry, index) => (
                                    <div
                                        key={index}
                                        className='legend-item'
                                    >
                                        <span
                                            className='legend-color'
                                            style={{ backgroundColor: entry.color }}
                                        ></span>
                                        <span className='legend-label'>{entry.name}</span>
                                        <span className='legend-value'>
                                            $
                                            {entry.value?.toLocaleString(undefined, {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Row - Clients */}
                {dashboardData.clientPortfolios?.length > 0 && (
                    <div className='flex-only'>
                        {/* Clients List Panel */}
                        <div className='dashboard__card clients-panel'>
                            <div className='panel-header'>
                                <h3>Clients List</h3>
                                {userObject && (
                                    <span
                                        onClick={() => navigate('/clients')}
                                        className='view-all'
                                    >
                                        View All
                                    </span>
                                )}
                            </div>
                            <div className='panel-subheader'>
                                <span>
                                    Manage client <strong>investments</strong> and track <strong>performance</strong>{' '}
                                    easily.
                                </span>
                            </div>
                            <div className='clients-list'>
                                {dashboardData.clientPortfolios.map((client: any) => (
                                    <div
                                        key={client.id}
                                        className='client-row'
                                        onClick={() => {
                                            setSelectedClientList(client);
                                            setPortfolioValue(client.portfolioValue);
                                        }}
                                    >
                                        <div className='client-info'>
                                            <div className='client-avatar'>
                                                {client.account.verificationRequest.firstName.charAt(0)}
                                            </div>
                                            <div className='client-details'>
                                                <div className='client-name'>Name</div>
                                                <div className='cash-value'>
                                                    {client.account.verificationRequest.firstName}{' '}
                                                    {client.account.verificationRequest.lastName}
                                                </div>
                                            </div>
                                        </div>
                                        <div className='client-value'>
                                            <div className='portfolio-value'>Portfolio Value</div>
                                            <div className='cash-value'>
                                                $
                                                {client.portfolioValue?.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Client Detail Panel */}
                        <div className='dashboard__card client-detail flex-7'>
                            <div className='client-header'>
                                <div className='client-profile'>
                                    <div className='client-avatar'>
                                        {selectedClientList?.account?.verificationRequest?.firstName.charAt(0)}
                                    </div>
                                    <h2>
                                        {selectedClientList?.account?.verificationRequest.firstName}{' '}
                                        {selectedClientList?.account?.verificationRequest.lastName}
                                    </h2>
                                </div>
                            </div>
                            <div className='client-portfolio'>
                                <div className='portfolio-value'>
                                    <span className='primary'>${portfolioInteger}</span>
                                    <span className='secondary'>.{portfolioDecimal}</span>
                                </div>
                                <div className='performance-indicators'>
                                    <div className='indicator'>
                                        <div className='indicator-value positive'>
                                            <span className='indicator-icon'>Best Performing: 🚀</span>
                                            <span className='stock-name'>{bestPerforming?.name}</span>
                                            <span className='stock-ticker'>({bestPerforming?.symbol})</span>
                                            <span className='stock-change'>
                                                {bestPerforming?.percentage.toFixed(2)}%
                                            </span>
                                        </div>
                                    </div>
                                    <div className='indicator'>
                                        <div className='indicator-value negative'>
                                            <span className='indicator-icon'>Worst Performing: 📉</span>
                                            <span className='stock-name'>{worstPerforming?.name}</span>
                                            <span className='stock-ticker'>({worstPerforming?.symbol})</span>
                                            <span className='stock-change'>
                                                {worstPerforming?.percentage.toFixed(2)}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className='holdings'>
                                <h3>Holdings</h3>
                                {selectedClientList?.account?.id && (
                                    <HoldingTable accountId={selectedClientList.account.id} />
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
